import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Bell, Volume2, Key, ShoppingCart, Calendar, Phone, ExternalLink, Copy } from "lucide-react";
import TemplateAuth from "./TemplateAuth";

// Left Container Component
const TemplateSetupForm = ({ 
  selectedCategory, 
  setSelectedCategory, 
  selectedOption, 
  setSelectedOption,
  categoryOptions,
  categoryIcons 
}) => {
  return (
    <div className="bg-white p-6 rounded-lg shadow-md w-full max-w-6xl">
      {/* Steps Header */}
      <div className="flex items-center space-x-4 border-b pb-4 mb-4">
        <span className="text-blue-600 font-semibold border-b-2 border-blue-600 pb-1">
          Set up template
        </span>
        <span className="text-gray-500">• Edit template</span>
        <span className="text-gray-500">• Submit for Review</span>
      </div>

      {/* Template Setup Form */}
      <h2 className="text-xl font-semibold mb-2">Set up your template</h2>
      <p className="text-gray-500 text-sm mb-4">
        Choose the category that best describes your message template. Then,
        select the type of message that you want to send.
        <span className="text-blue-500 cursor-pointer">
          {" "}
          Learn more about categories.
        </span>
      </p>

      {/* Category Selection */}
      <div className="flex space-x-2 mb-4">
        {["Marketing", "Utility", "Authentication"].map((category) => {
          const Icon = categoryIcons[category];
          return (
            <button
              key={category}
              className={`px-4 py-2 rounded flex-1 text-sm font-semibold flex items-center justify-center gap-2 ${
                selectedCategory === category
                  ? "bg-blue-600 text-white"
                  : "bg-gray-200 text-gray-600 hover:bg-gray-300"
              }`}
              onClick={() => setSelectedCategory(category)}
            >
              <Icon size={16} strokeWidth={2} />
              {category}
            </button>
          );
        })}
      </div>

      {/* Message Type Selection */}
      <div className="bg-gray-50 p-4 rounded-lg mb-6 border w-full">
        {categoryOptions[selectedCategory].map((option) => (
          <div
            key={option.label}
            className={`flex items-start p-3 rounded-md cursor-pointer mb-2 ${
              selectedOption === option.label
                ? "bg-blue-100 border border-blue-400"
                : "hover:bg-gray-100"
            }`}
            onClick={() => setSelectedOption(option.label)}
          >
            <input
              type="radio"
              name="messageType"
              checked={selectedOption === option.label}
              onChange={() => setSelectedOption(option.label)}
              className="mt-1 mr-3 w-5 h-5 accent-blue-600 cursor-pointer"
            />
            <div>
              <p className="font-medium text-sm">{option.label}</p>
              <p className="text-xs text-gray-500">{option.desc}</p>
            </div>
          </div>
        ))}
      </div>

     <div className="flex justify-between">
  <button className="px-4 py-2 border rounded-md text-gray-600 hover:bg-gray-50">
    Discard
  </button>

  

  {selectedCategory === "Authentication" ? (
     <Link to="/TemplateAuth">
      <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
        Next
      </button>
    </Link>
  ) : (
    <Link to="/TemplateSetup/template">
      <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
        Next
      </button>
    </Link>
  )}
</div>
    </div>
  );
};

// Right Container Component
const TemplatePreview = ({ selectedCategory, selectedOption }) => {
  const getTemplatePreview = () => {
    switch (selectedCategory) {
      case "Marketing":
        return {
          image: "https://images.unsplash.com/photo-1542838132-92c53300491e?w=400&h=200&fit=crop",
          title: "🛍️ Fresh Groceries Available!",
          message: "Hey there! Check out our fresh groceries now!",
          subtitle: `Use code HEALTH to get additional 10% off on your entire purchase.`,
          buttons: [
            { icon: <ExternalLink size={14} />, text: "Shop now", color: "text-blue-600" },
            { icon: <Copy size={14} />, text: "Copy code", color: "text-blue-600" }
          ],
          goodFor: "Welcome messages, promotions, offers, coupons, newsletters, announcements",
          customizable: "Header, Body, Footer, Button"
        };
      
      case "Utility":
        return {
          image: "https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=400&h=200&fit=crop",
          title: "📦 Order Update",
          message: "Your order #12345 has been shipped!",
          subtitle: "Expected delivery: Tomorrow by 6 PM. Track your package below.",
          buttons: [
            { icon: <ExternalLink size={14} />, text: "Track package", color: "text-green-600" },
            { icon: <Phone size={14} />, text: "Contact support", color: "text-green-600" }
          ],
          goodFor: "Order updates, appointment reminders, shipping notifications, account alerts",
          customizable: "Header, Body, Footer, Button"
        };
      
      case "Authentication":
        return {
          image: null,
          title: "🔐 Security Code",
          message: "Your WhatsApp verification code is:",
          subtitle: "Do not share this code with anyone. It expires in 10 minutes.",
          code: "847293",
          buttons: [],
          goodFor: "Login verification, two-factor authentication, password resets",
          customizable: "Body, Footer"
        };
      
      default:
        return {
          image: "https://images.unsplash.com/photo-1542838132-92c53300491e?w=400&h=200&fit=crop",
          title: "Sample Template",
          message: "This is a sample message",
          subtitle: "",
          buttons: [],
          goodFor: "",
          customizable: ""
        };
    }
  };

  const template = getTemplatePreview();

  return (
    <div className="bg-white p-6 rounded-lg shadow-md w-full max-w-sm border border-gray-300 sticky top-6">
      <h3 className="text-lg font-semibold mb-4">Template previewss</h3>
      <div className="bg-gray-100 p-6 rounded-lg shadow-inner">
        <div className="border rounded-lg overflow-hidden shadow-md bg-white">
          {template.image && (
            <img
              src={template.image}
              alt="Template preview"
              className="w-full h-40 object-cover"
            />
          )}
          <div className="p-4">
            <p className="text-sm font-medium mb-2">
              {template.title}
            </p>
            <p className="text-sm mb-2">
              {template.message}
            </p>
            {template.code && (
              <div className="bg-gray-100 p-3 rounded text-center my-3">
                <span className="text-2xl font-bold text-blue-600">{template.code}</span>
              </div>
            )}
            <p className="text-sm text-gray-600">
              {template.subtitle}
            </p>
            <p className="text-xs text-gray-500 text-right mt-2">11:59</p>
          </div>
          {template.buttons.length > 0 && (
            <div className="border-t">
              {template.buttons.map((button, index) => (
                <button
                  key={index}
                  className={`w-full flex items-center justify-center gap-2 py-2 font-medium hover:bg-gray-100 ${
                    index > 0 ? 'border-t' : ''
                  } ${button.color}`}
                >
                  {button.icon}
                  {button.text}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="mt-6 text-gray-700 leading-relaxed">
        <p className="text-base font-semibold tracking-wide mb-1">
          This template is good for
        </p>
        <p className="text-sm text-gray-600 mb-4">
          {template.goodFor}
        </p>

        <p className="text-base font-semibold tracking-wide mb-1">
          Template areas that you can customize
        </p>
        <p className="text-sm text-gray-600">
          {template.customizable}
        </p>
      </div>
    </div>
  );
};

// Main Component
const TemplateSetup = () => {
  const [selectedCategory, setSelectedCategory] = useState("Marketing");
  const [selectedOption, setSelectedOption] = useState("Default");

  const categoryIcons = {
    Marketing: Bell,
    Utility: Volume2,
    Authentication: Key,
  };

  const categoryOptions = {
    Marketing: [
      {
        label: "Default",
        desc: "Send messages with media and customised buttons to engage your customers.",
      },
      {
        label: "Catalogue",
        desc: "Send messages that drive sales by connecting your product catalogue.",
      },
      {
        label: "Flows",
        desc: "Send a form to capture customer interests, appointment requests or run surveys.",
      },
      {
        label: "Calling permissions request",
        desc: "Ask customers if you can call them on WhatsApp.",
      },
    ],
    Utility: [
      {
        label: "Default",
        desc: "Send messages with media and customised buttons to engage your customers.",
      },
      {
        label: "Catalogue",
        desc: "Send messages that drive sales by connecting your product catalogue.",
      },
      {
        label: "Flows",
        desc: "Send a form to capture customer interests, appointment requests or run surveys.",
      },
    ],
    Authentication: [
      {
        label: "OTP",
        desc: "One-time password for secure authentication.",
      },
    ],
  };

  return (
    <div className="h-full bg-gradient-to-b from-blue-50 to-white p-6 flex gap-10 items-start">
      {/* Left Side Form */}
      <TemplateSetupForm
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
        selectedOption={selectedOption}
        setSelectedOption={setSelectedOption}
        categoryOptions={categoryOptions}
        categoryIcons={categoryIcons}
      />

      {/* Right Side Preview */}
      <TemplatePreview
        selectedCategory={selectedCategory}
        selectedOption={selectedOption}
      />
    </div>
  );
};

export default TemplateSetup;